import { useState } from "react";
import { Star, Trash2, GripVertical } from "lucide-react";

/**
 * ProductImageGallery - Editable grid of product images with drag&drop reordering.
 * - The FIRST image in the list is always the "main" image.
 * - Drag-and-drop (desktop) or "Hacer principal" button (mobile) to reorder.
 *
 * Props:
 *   images: string[]              - full ordered list (main + additional)
 *   onReorder: (newImages) => void - called with the new ordered array
 *   onRemove: (index) => void     - called to delete the image at index
 */
export const ProductImageGallery = ({ images, onReorder, onRemove }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  if (!images || images.length === 0) return null;

  const handleDragStart = (e, idx) => {
    setDraggedIndex(idx);
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* noop */ }
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== idx) setDragOverIndex(idx);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIdx) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIdx) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    const next = [...images];
    const [moved] = next.splice(draggedIndex, 1);
    next.splice(targetIdx, 0, moved);
    onReorder(next);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const makeMain = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    const [moved] = next.splice(idx, 1);
    next.unshift(moved);
    onReorder(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <GripVertical size={14} />
        <span>Arrastra para reordenar. La 1ra imagen es la <strong>principal</strong>.</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="product-image-gallery">
        {images.map((src, idx) => {
          const isMain = idx === 0;
          const isDragging = draggedIndex === idx;
          const isDropTarget = dragOverIndex === idx && draggedIndex !== idx;
          return (
            <div
              key={`${src}-${idx}`}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              className={`relative aspect-square rounded-xl overflow-hidden border-2 cursor-move transition-all select-none ${
                isMain ? "border-amber-400 shadow-md" : "border-[#E2E8F0]"
              } ${isDragging ? "opacity-40 scale-95" : ""} ${
                isDropTarget ? "ring-2 ring-blue-400 ring-offset-1" : ""
              }`}
              data-testid={`gallery-item-${idx}`}
            >
              <img src={src} alt={`Imagen ${idx + 1}`} className="w-full h-full object-cover pointer-events-none" />

              {/* Main badge */}
              {isMain && (
                <div className="absolute top-1 left-1 bg-amber-400 text-amber-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm">
                  <Star size={9} className="fill-amber-900" />
                  PRINCIPAL
                </div>
              )}

              {/* Position number (for accessibility) */}
              {!isMain && (
                <div className="absolute top-1 left-1 bg-black/50 text-white text-[9px] font-semibold w-5 h-5 rounded-full flex items-center justify-center">
                  {idx + 1}
                </div>
              )}

              {/* Delete button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(idx);
                }}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-sm"
                title="Eliminar imagen"
                data-testid={`gallery-remove-${idx}`}
              >
                <Trash2 size={11} />
              </button>

              {/* "Hacer principal" button - mobile-friendly alt to drag */}
              {!isMain && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    makeMain(idx);
                  }}
                  className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] py-1 hover:bg-black/80 transition-colors"
                  title="Convertir en imagen principal"
                  data-testid={`gallery-make-main-${idx}`}
                >
                  Hacer principal
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductImageGallery;
